import React, { useState } from "react";
import { Crown } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { C } from "../constants/design";
import { playerLevel, MAX_LEVEL } from "../engine/playerEngine";
import { SLOT_DEFS, STATS, LAYER_IMGS, PP_IMGS, BACKGROUND_IMGS } from "../constants/items";
import { StatIcon } from "./ui/StatIcon";
import { SH } from "./ui/atoms";
import { ItemToken, SlotToken } from "./ui/ItemCard";
import { ItemModal } from "./ui/ItemModal";
import type { Item } from "../types/game";
import { iSkin } from "../engine/itemEngine";
import {
  reputationToRank,
  getRankConfig,
  RANK_DISPLAY_THEME,
} from "../features/league-duel/engine/duelRanking";
import { getTier, actForTier } from "../features/stadium-trial/content/pveTiers";

export interface PlayerRow {
  player: {
    name: string;
    avatar?: string;
    cups?: number;
    inventory?: Item[];
    equipped?: Record<string, string | null>;
    duel_reputation?: number | null;
    duel_rank?: string | null;
    duel_mmr?: number | null;
    duel_xp?: number | null;
    pve_current_tier?: number | null;
    pve_best_tier?: number | null;
    pve_best_stage?: number | null;
  };
  derived: {
    earned: number;
    won: number;
    total: number;
    winrate: number;
    power: number;
    stats: Record<string, number>;
  };
}

function friendsKey(myName: string) {
  return `friends_list_${myName}`;
}
function loadFriends(myName: string): string[] {
  try { return JSON.parse(localStorage.getItem(friendsKey(myName)) || "[]"); } catch { return []; }
}
function saveFriends(myName: string, list: string[]) {
  localStorage.setItem(friendsKey(myName), JSON.stringify(list));
}

const RANK_COLORS: Record<string, string> = {
  Bronze:   "#CD7F32",
  Silver:   "#A8B8C8",
  Gold:     "#F5B301",
  Platinum: "#C8D0D8",
  Emerald:  "#10C068",
  Ruby:     "#E0115F",
  Diamond:  "#7DD3FC",
};
function rankColor(label: string): string {
  return RANK_COLORS[label] ?? C.muted;
}

// Stadium Trial accent color = the tier's ACT (rank rarity), so the row colour
// changes once every 10 tiers (per act) instead of on every tier. Single source
// of truth: PVE_ACTS in pveTiers.ts.
function tierColor(tier: number): string {
  return actForTier(tier).color ?? C.muted;
}
// Current tier the player sits at (ranking uses best_stage as tiebreak within a tier).
function playerTier(p: PlayerRow["player"]): number {
  return p.pve_current_tier ?? p.pve_best_tier ?? 1;
}
function stadiumScore(p: PlayerRow["player"]): number {
  return playerTier(p) * 100 + (p.pve_best_stage ?? 0);
}
function hasPlayedStadium(p: PlayerRow["player"]): boolean {
  return (p.pve_best_tier ?? p.pve_current_tier ?? 1) > 1 || (p.pve_best_stage ?? 0) > 0;
}

export function Classement({
  players,
  myName,
  loading = false,
  onInspect,
  focusTab,
  onFocusConsumed,
}: {
  players: PlayerRow[];
  myName?: string;
  loading?: boolean;
  onInspect?: (row: PlayerRow) => void;
  /** Deep-link (ex: bouton Lobby) : force l'onglet actif à l'ouverture. */
  focusTab?: "picks" | "duel" | "stadium" | null;
  onFocusConsumed?: () => void;
}) {
  const { t } = useI18n();
  const medals = ["#F5B301", "#C9D2E0", "#CD7F32"];

  const [primaryTab, setPrimaryTab] = useState<"picks" | "duel" | "stadium">("picks");

  // Le widget reste monté (CSS display) → l'effet s'applique même masqué, dès que
  // le Lobby demande un onglet précis (ex: "duel" pour matcher le rang affiché).
  React.useEffect(() => {
    if (focusTab) {
      setPrimaryTab(focusTab);
      onFocusConsumed?.();
    }
  }, [focusTab]); // eslint-disable-line
  const [activeTab, setActiveTab] = useState<"global" | "friends">("global");
  const [friends, setFriends] = useState<string[]>(() =>
    myName ? loadFriends(myName) : []
  );

  const toggleFriend = (name: string) => {
    if (!myName) return;
    const next = friends.includes(name)
      ? friends.filter((f) => f !== name)
      : [...friends, name];
    setFriends(next);
    saveFriends(myName, next);
  };

  const filteredPlayers =
    activeTab === "friends"
      ? players.filter((r) => friends.includes(r.player.name) || r.player.name === myName)
      : players;

  const displayPlayers =
    primaryTab === "picks"
      ? filteredPlayers
      : primaryTab === "duel"
        ? [...filteredPlayers].sort((a, b) => {
            const repA = (a.player.duel_reputation) ?? -1;
            const repB = (b.player.duel_reputation) ?? -1;
            return repB - repA;
          })
        : [...filteredPlayers]
            .filter((r) => hasPlayedStadium(r.player))
            .sort((a, b) => stadiumScore(b.player) - stadiumScore(a.player));

  const rankDefs = getRankConfig(RANK_DISPLAY_THEME);
  const getRankDef = (label: string) => rankDefs.find((r) => r.label === label) ?? rankDefs[0];

  return (
    <div>
      <SH title={t("classement.title")} />

      {/* ── Primary Tabs ─────────────────────────────────────────────────── */}
      <div style={{
        display: "flex",
        gap: 0,
        marginBottom: 10,
        background: C.bg2,
        borderRadius: 13,
        padding: 3,
      }}>
        {(["picks", "duel", "stadium"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setPrimaryTab(tab)}
            style={{
              flex: 1,
              border: "none",
              borderRadius: 10,
              padding: "10px 0",
              fontWeight: 800,
              fontSize: 13,
              background: primaryTab === tab ? C.surface : "transparent",
              color: primaryTab === tab ? C.text : C.muted,
              cursor: "pointer",
              boxShadow: primaryTab === tab ? "0 1px 5px rgba(0,0,0,0.35)" : "none",
              transition: "background .15s, color .15s, box-shadow .15s",
            }}
          >
            {tab === "picks"
              ? t("classement.picksTab")
              : tab === "duel"
                ? t("classement.duelTab")
                : t("classement.stadiumTab")}
          </button>
        ))}
      </div>

      {/* ── Secondary Tabs (Global / Friends) ────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {(["global", "friends"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              border: `1px solid ${activeTab === tab ? C.turf : C.line}`,
              borderRadius: 10,
              padding: "7px 0",
              fontWeight: 800,
              fontSize: 12,
              background: activeTab === tab ? `${C.turf}18` : "transparent",
              color: activeTab === tab ? C.turf : C.muted,
              cursor: "pointer",
            }}
          >
            {tab === "global" ? t("classement.globalTab") : t("classement.friendsTab", friends.length)}
          </button>
        ))}
      </div>

      {/* ── Skeleton (premier chargement) ─────────────────────────────────── */}
      {loading && players.length === 0 &&
        Array.from({ length: 6 }).map((_, i) => (
          <div
            key={`skel-${i}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "11px 12px",
              marginBottom: 8,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 13,
            }}
          >
            <div className="skel" style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }} />
            <div className="skel" style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="skel" style={{ width: "55%", height: 13, borderRadius: 4 }} />
              <div className="skel" style={{ width: "35%", height: 10, borderRadius: 4 }} />
            </div>
            <div className="skel" style={{ width: 40, height: 20, borderRadius: 5, flexShrink: 0 }} />
          </div>
        ))}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {!loading && displayPlayers.length === 0 && (
        <div style={{ textAlign: "center", color: C.faint, padding: "40px 10px" }}>
          {activeTab === "friends"
            ? t("classement.noFriendsYet")
            : primaryTab === "duel"
              ? t("classement.noFiveDuelPlayers")
              : primaryTab === "stadium"
                ? t("classement.noStadiumPlayers")
                : t("classement.makeFirstPredictions")}
        </div>
      )}

      {/* ── Picks Points rows ─────────────────────────────────────────────── */}
      {primaryTab === "picks" && displayPlayers.map((row, i) => {
        const mine = row.player.name === myName;
        const d = row.derived;
        const av = row.player.avatar;
        const lvl = playerLevel(d.earned + (row.player.duel_xp ?? 0));
        const isMaxLevel = lvl >= MAX_LEVEL;

        return (
          <div
            key={row.player.name}
            onClick={() => onInspect?.(row)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "11px 12px",
              marginBottom: 8,
              background: mine ? `${C.turf}12` : C.surface,
              border: `1px solid ${mine ? C.turf : C.line}`,
              borderRadius: 13,
              cursor: "pointer",
            }}
          >
            <div style={{ width: 26, textAlign: "center", fontWeight: 900, fontSize: 15, color: i < 3 ? medals[i] : C.faint, flexShrink: 0 }}>
              {i < 3 ? <Crown size={18} color={medals[i]} style={{ verticalAlign: "middle" }} /> : i + 1}
            </div>

            <div style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${C.line}`, background: C.bg2, display: "grid", placeItems: "center", overflow: "hidden", position: "relative", flexShrink: 0 }}>
              {av && (PP_IMGS[av] ?? LAYER_IMGS[av]) ? (
                <img src={PP_IMGS[av] ?? LAYER_IMGS[av]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 16 }}>👤</span>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14.5, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.player.name}</span>
                {mine && (
                  <span style={{ fontSize: 9.5, fontWeight: 800, color: C.turf, border: `1px solid ${C.turf}`, borderRadius: 5, padding: "0 4px" }}>
                    {t("classement.youBadge")}
                  </span>
                )}
                {(row.player.cups || 0) > 0 && (
                  <span style={{ fontSize: 11, color: C.gold }} title={t("classement.kLeagueTrophiesTooltip")}>
                    {"🏆".repeat(Math.min(row.player.cups!, 5))}
                    {(row.player.cups || 0) > 5 ? `×${row.player.cups}` : ""}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: isMaxLevel ? C.gold : C.muted, background: isMaxLevel ? `${C.gold}18` : "rgba(255,255,255,0.06)", border: `1px solid ${isMaxLevel ? C.gold : "rgba(255,255,255,0.1)"}`, borderRadius: 5, padding: "0px 5px", flexShrink: 0 }}>
                  {isMaxLevel ? t("classement.max") : t("classement.level", lvl)}
                </span>
                <span>{Math.round(d.winrate * 100)}% · ⚡{d.power}</span>
              </div>
            </div>

            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div className="d" style={{ fontWeight: 900, fontSize: 17, color: C.turf }}>
                {d.earned.toLocaleString("en-US")}
              </div>
              <div style={{ fontSize: 10, color: C.faint, fontWeight: 700 }}>{t("classement.points")}</div>
            </div>
          </div>
        );
      })}

      {/* ── Five Duel rows ────────────────────────────────────────────────── */}
      {primaryTab === "duel" && displayPlayers.map((row, i) => {
        const mine = row.player.name === myName;
        const av = row.player.avatar;
        const rep = row.player.duel_reputation ?? 0;
        const rankLabel = reputationToRank(rep);
        const rankDef = getRankDef(rankLabel);
        const rColor = rankColor(rankLabel);
        const lvl = playerLevel(row.derived.earned + (row.player.duel_xp ?? 0));
        const isMaxLevel = lvl >= MAX_LEVEL;

        return (
          <div
            key={row.player.name}
            onClick={() => onInspect?.(row)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "11px 12px",
              marginBottom: 8,
              background: mine ? `${rColor}12` : C.surface,
              border: `1px solid ${mine ? rColor : C.line}`,
              borderRadius: 13,
              cursor: "pointer",
            }}
          >
            {/* Position */}
            <div style={{ width: 26, textAlign: "center", fontWeight: 900, fontSize: 15, color: i < 3 ? medals[i] : C.faint, flexShrink: 0 }}>
              {i < 3 ? <Crown size={18} color={medals[i]} style={{ verticalAlign: "middle" }} /> : i + 1}
            </div>

            {/* Avatar */}
            <div style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${C.line}`, background: C.bg2, display: "grid", placeItems: "center", overflow: "hidden", flexShrink: 0 }}>
              {av && (PP_IMGS[av] ?? LAYER_IMGS[av]) ? (
                <img src={PP_IMGS[av] ?? LAYER_IMGS[av]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 16 }}>👤</span>
              )}
            </div>

            {/* Name + level */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14.5, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.player.name}</span>
                {mine && (
                  <span style={{ fontSize: 9.5, fontWeight: 800, color: rColor, border: `1px solid ${rColor}`, borderRadius: 5, padding: "0 4px" }}>
                    {t("classement.youBadge")}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: isMaxLevel ? C.gold : C.muted, background: isMaxLevel ? `${C.gold}18` : "rgba(255,255,255,0.06)", border: `1px solid ${isMaxLevel ? C.gold : "rgba(255,255,255,0.1)"}`, borderRadius: 5, padding: "0px 5px", flexShrink: 0 }}>
                  {isMaxLevel ? t("classement.max") : t("classement.level", lvl)}
                </span>
                <span style={{ color: rColor, fontWeight: 700 }}>{rankDef.icon} {rankLabel}</span>
              </div>
            </div>

            {/* RPT score */}
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div className="d" style={{ fontWeight: 900, fontSize: 17, color: rColor }}>
                {rep}
              </div>
              <div style={{ fontSize: 10, color: C.faint, fontWeight: 700 }}>RPT</div>
            </div>
          </div>
        );
      })}

      {/* ── Stadium Trial rows ────────────────────────────────────────────── */}
      {primaryTab === "stadium" && displayPlayers.map((row, i) => {
        const mine = row.player.name === myName;
        const av = row.player.avatar;
        const tier = playerTier(row.player);
        const tierName = getTier(tier)?.tierName ?? `Tier ${tier}`;
        const act = actForTier(tier);
        const tColor = act.color;
        const bestStage = row.player.pve_best_stage ?? 0;
        const lvl = playerLevel(row.derived.earned + (row.player.duel_xp ?? 0));
        const isMaxLevel = lvl >= MAX_LEVEL;

        return (
          <div
            key={row.player.name}
            onClick={() => onInspect?.(row)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "11px 12px",
              marginBottom: 8,
              background: mine ? `${tColor}12` : C.surface,
              border: `1px solid ${mine ? tColor : C.line}`,
              borderRadius: 13,
              cursor: "pointer",
            }}
          >
            {/* Position */}
            <div style={{ width: 26, textAlign: "center", fontWeight: 900, fontSize: 15, color: i < 3 ? medals[i] : C.faint, flexShrink: 0 }}>
              {i < 3 ? <Crown size={18} color={medals[i]} style={{ verticalAlign: "middle" }} /> : i + 1}
            </div>

            {/* Avatar */}
            <div style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${C.line}`, background: C.bg2, display: "grid", placeItems: "center", overflow: "hidden", flexShrink: 0 }}>
              {av && (PP_IMGS[av] ?? LAYER_IMGS[av]) ? (
                <img src={PP_IMGS[av] ?? LAYER_IMGS[av]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 16 }}>👤</span>
              )}
            </div>

            {/* Name + tier name */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14.5, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.player.name}</span>
                {mine && (
                  <span style={{ fontSize: 9.5, fontWeight: 800, color: tColor, border: `1px solid ${tColor}`, borderRadius: 5, padding: "0 4px" }}>
                    {t("classement.youBadge")}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: isMaxLevel ? C.gold : C.muted, background: isMaxLevel ? `${C.gold}18` : "rgba(255,255,255,0.06)", border: `1px solid ${isMaxLevel ? C.gold : "rgba(255,255,255,0.1)"}`, borderRadius: 5, padding: "0px 5px", flexShrink: 0 }}>
                  {isMaxLevel ? t("classement.max") : t("classement.level", lvl)}
                </span>
                <span style={{ color: tColor, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {tierName}
                  {bestStage > 0 ? ` · ${bestStage}/10` : ""}
                </span>
              </div>
            </div>

            {/* Tier badge */}
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div className="d" style={{ fontWeight: 900, fontSize: 17, color: tColor }}>
                T{tier}
              </div>
              <div style={{ fontSize: 10, color: tColor, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase" }}>{act.name}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PlayerInspectModal({
  row,
  myStats = {},
  myName,
  loading = false,
  onClose,
}: {
  row: PlayerRow;
  myStats?: Record<string, number>;
  myName?: string;
  loading?: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const p = row.player;
  const d = row.derived;
  const inv = p.inventory || [];
  const equipped = p.equipped || {};
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);

  const CHARACTER_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/Character.png";
  const AVATAR_WIDTH = 265;

  const layerImg = (slot: string) => {
    const iid = equipped[slot];
    if (!iid) return null;
    const it = inv.find((x) => x.iid === iid);
    return it ? (LAYER_IMGS[iSkin(it)] ?? null) : null;
  };
  const bgIid = equipped["background"];
  const bgItem = bgIid ? inv.find((x) => x.iid === bgIid) : null;
  const backgroundImg = bgItem ? BACKGROUND_IMGS[iSkin(bgItem)] : null;

  const isOwnProfile = p.name === myName;
  const [isFriend, setIsFriend] = useState(() => {
    if (!myName || isOwnProfile) return false;
    try { return JSON.parse(localStorage.getItem(`friends_list_${myName}`) || "[]").includes(p.name); }
    catch { return false; }
  });
  const toggleFollow = () => {
    if (!myName || isOwnProfile) return;
    const key = `friends_list_${myName}`;
    const list: string[] = (() => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } })();
    const next = isFriend ? list.filter((n) => n !== p.name) : [...list, p.name];
    localStorage.setItem(key, JSON.stringify(next));
    setIsFriend(!isFriend);
  };

  const equippedBySlot: Record<string, Item | null> = {};
  SLOT_DEFS.forEach((slot) => {
    const iid = equipped[slot.key];
    equippedBySlot[slot.key] = iid ? (inv.find((x) => x.iid === iid) ?? null) : null;
  });

  const lvl = playerLevel(d.earned + (p.duel_xp ?? 0));
  const isMaxLevel = lvl >= MAX_LEVEL;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: C.bg,
        overflowY: "auto",
        animation: "pageSlideUp .28s cubic-bezier(.32,.72,0,1)",
      }}
    >
      {/* Top bar */}
      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        background: C.bg,
        borderBottom: `1px solid ${C.line}`,
      }}>
        <button
          onClick={onClose}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: "none",
            color: C.muted, fontWeight: 800, fontSize: 14,
            cursor: "pointer", padding: "4px 0",
          }}
        >
          {t("common.back")}
        </button>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 900, fontSize: 16, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {p.name}
        </div>
        {!isOwnProfile && myName ? (
          <button
            onClick={toggleFollow}
            style={{
              border: `1px solid ${isFriend ? C.turf : C.line}`,
              borderRadius: 10, padding: "6px 12px",
              fontWeight: 800, fontSize: 12,
              background: isFriend ? `${C.turf}18` : "transparent",
              color: isFriend ? C.turf : C.muted,
              cursor: "pointer", flexShrink: 0,
            }}
          >
            {isFriend ? t("classement.following") : t("classement.follow")}
          </button>
        ) : (
          <div style={{ width: 80 }} />
        )}
      </div>

      <div style={{ padding: "16px 16px 48px", maxWidth: 480, margin: "0 auto" }}>

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 16 }}>
          {[
            { v: d.earned.toLocaleString("en-US"), l: t("classement.points"), c: C.turf },
            { v: `${Math.round(d.winrate * 100)}%`, l: t("classement.winrate"), c: null },
            { v: `${d.won}/${d.total}`, l: t("classement.pronos"), c: null },
            { v: loading ? <span className="skel" style={{ display: "inline-block", width: 26, height: 14, borderRadius: 4 }} /> : d.power, l: t("classement.power"), c: C.gold },
          ].map(({ v, l, c }) => (
            <div key={l} style={{ background: C.surface, borderRadius: 10, padding: "9px 4px", textAlign: "center", border: `1px solid ${C.line}` }}>
              <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 14, color: c || C.text, minHeight: 17 }}>{v}</div>
              <div style={{ fontSize: 8, color: C.faint, fontWeight: 800, marginTop: 2, letterSpacing: 0.8 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Level badge + duel rank */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: isMaxLevel ? C.gold : C.muted, background: isMaxLevel ? `${C.gold}18` : "rgba(255,255,255,0.06)", border: `1px solid ${isMaxLevel ? C.gold : "rgba(255,255,255,0.1)"}`, borderRadius: 6, padding: "2px 7px" }}>
            {isMaxLevel ? t("classement.max") : t("classement.level", lvl)}
          </span>
          {p.duel_rank && (() => {
            const rDef = getRankConfig(RANK_DISPLAY_THEME).find(r => r.label === p.duel_rank);
            const rCol = RANK_COLORS[p.duel_rank] ?? C.muted;
            return (
              <span style={{ fontSize: 11, fontWeight: 800, color: rCol, background: `${rCol}18`, border: `1px solid ${rCol}40`, borderRadius: 6, padding: "2px 7px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                {rDef?.icon} {p.duel_rank} · {p.duel_reputation ?? 0} RPT
              </span>
            );
          })()}
          {(p.cups || 0) > 0 && (
            <span style={{ fontSize: 12, color: C.gold }}>
              {"🏆".repeat(Math.min(p.cups!, 5))}{(p.cups || 0) > 5 ? `×${p.cups}` : ""}
            </span>
          )}
        </div>

        {/* ── Character card — fond isolé (overflow:hidden) + grid libre ── */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          {/* Fond clippé séparément pour le border-radius — n'empêche pas le grid de déborder */}
          <div style={{
            position: "absolute", inset: 0,
            borderRadius: 18,
            overflow: "hidden",
            border: `1px solid ${C.line}`,
            background: C.surface,
            zIndex: 0,
          }}>
            {backgroundImg ? (
              <img src={backgroundImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85, pointerEvents: "none" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 40%, ${C.turf}1F, ${C.surface}26 60%, transparent 80%)` }} />
            )}
          </div>

          {/* Grid — pas d'overflow:hidden, le personnage déborde librement vers le haut */}
          <div style={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: "80px 1fr 80px",
            gap: 6,
            alignItems: "center",
            padding: "8px 8px 14px",
          }}>
            {/* Left slots — z:3 toujours au-dessus du personnage */}
            <div style={{ position: "relative", zIndex: 3, display: "flex", flexDirection: "column", gap: 8 }}>
              {SLOT_DEFS.slice(0, 3).map((slot) =>
                loading ? (
                  <div key={slot.key} className="skel" style={{ aspectRatio: "1", borderRadius: 12 }} />
                ) : (
                  <SlotToken
                    key={slot.key}
                    slotDef={slot}
                    equippedItem={equippedBySlot[slot.key]}
                    onClick={equippedBySlot[slot.key] ? () => setSelectedItem(equippedBySlot[slot.key]) : undefined}
                  />
                )
              )}
            </div>

            {/* Center: personnage — z:1 sous les slots, minWidth:0 anti-overflow */}
            <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "stretch", minWidth: 0 }}>
              <div style={{ position: "relative", width: AVATAR_WIDTH, marginTop: -18, marginLeft: -15, flexShrink: 0 }}>
                <img src={CHARACTER_IMG} alt="" style={{ display: "block", width: "100%", height: "auto", visibility: "hidden" }} />
                {layerImg("ball") && <img src={layerImg("ball")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 0, pointerEvents: "none" }} />}
                {layerImg("familiar") && <img src={layerImg("familiar")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 1, pointerEvents: "none" }} />}
                <img src={CHARACTER_IMG} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 2, pointerEvents: "none" }} />
                {layerImg("shoes") && <img src={layerImg("shoes")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 3, pointerEvents: "none" }} />}
                {layerImg("top") && <img src={layerImg("top")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 4, pointerEvents: "none" }} />}
                {layerImg("head") && <img src={layerImg("head")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 5, pointerEvents: "none" }} />}
              </div>
            </div>

            {/* Right slots — z:3 toujours au-dessus du personnage */}
            <div style={{ position: "relative", zIndex: 3, display: "flex", flexDirection: "column", gap: 8 }}>
              {SLOT_DEFS.slice(3, 6).map((slot) =>
                loading ? (
                  <div key={slot.key} className="skel" style={{ aspectRatio: "1", borderRadius: 12 }} />
                ) : (
                  <SlotToken
                    key={slot.key}
                    slotDef={slot}
                    equippedItem={equippedBySlot[slot.key]}
                    onClick={equippedBySlot[slot.key] ? () => setSelectedItem(equippedBySlot[slot.key]) : undefined}
                  />
                )
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div style={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>
          {t("classement.statistics")}
        </div>
        <div style={{ background: C.surface, borderRadius: 13, padding: "12px 14px" }}>
          {STATS.map((st) => {
            const val = d.stats[st.key] || 0;
            const mine = myStats[st.key] || 0;
            const diff = mine - val;
            const diffColor = diff > 0 ? C.turf : diff < 0 ? "#e06a8c" : C.faint;
            const diffLabel = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : "=";
            return (
              <div key={st.key} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatIcon st={st} size={13} />
                  <span style={{ fontSize: 12, color: C.muted, flex: 1, fontWeight: 700 }}>{st.label}</span>
                  {loading ? (
                    <span className="skel" style={{ width: 18, height: 13, borderRadius: 4 }} />
                  ) : (
                    <span style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 13, color: val > 0 ? st.color : C.faint }}>{val}</span>
                  )}
                  {!loading && myName !== p.name && (
                    <span style={{ fontSize: 11, fontWeight: 800, color: diffColor, minWidth: 28, textAlign: "right" }}>{diffLabel}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedItem && (
        <ItemModal
          it={selectedItem}
          isEquipped={true}
          onEquip={() => {}}
          onUnequip={() => {}}
          onSell={() => {}}
          onClose={() => setSelectedItem(null)}
          readOnly
        />
      )}
    </div>
  );
}
